import React, { useEffect, useMemo, useRef } from "react";
import { Stack } from "react-bootstrap";
import { colors } from "src/styles";
import styled from "styled-components";
import * as d3 from "d3";

interface WidgetProps {
  caption: string;
}

interface CompletedWorkData {
  month: string;
  completedCount: number;
}

const InternallyCompletedWorkChart = ({ caption }: WidgetProps) => {
  const svgRef = useRef<SVGSVGElement>(null);

  const completedWorkData: CompletedWorkData[] = useMemo(
    () => [
      { month: "Jan", completedCount: 18 },
      { month: "Feb", completedCount: 22 },
      { month: "Mar", completedCount: 20 },
      { month: "Apr", completedCount: 25 },
      { month: "May", completedCount: 23 },
      { month: "Jun", completedCount: 28 },
      { month: "Jul", completedCount: 30 },
      { month: "Aug", completedCount: 27 },
      { month: "Sep", completedCount: 24 },
      { month: "Oct", completedCount: 26 },
      { month: "Nov", completedCount: 29 },
      { month: "Dec", completedCount: 31 },
    ],
    [],
  );

  useEffect(() => {
    if (!svgRef.current || completedWorkData.length === 0) return;

    const margin = { top: 20, right: 20, bottom: 40, left: 56 };
    const width = 500 - margin.left - margin.right;
    const height = 250 - margin.top - margin.bottom;

    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3
      .select(svgRef.current)
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const x = d3
      .scaleBand()
      .domain(completedWorkData.map((d) => d.month))
      .range([0, width])
      .padding(0.3);

    const maxCompleted = d3.max(completedWorkData, (d) => d.completedCount) || 0;
    const y = d3
      .scaleLinear()
      .domain([0, maxCompleted + 5])
      .range([height, 0]);

    svg
      .append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(x))
      .selectAll("text")
      .style("font-size", "12px");

    svg.append("g").call(d3.axisLeft(y).ticks(6)).selectAll("text").style("font-size", "12px");

    svg
      .selectAll(".bar")
      .data(completedWorkData)
      .enter()
      .append("rect")
      .attr("class", "bar")
      .attr("x", (d) => x(d.month) || 0)
      .attr("y", height)
      .attr("width", x.bandwidth())
      .attr("height", 0)
      .attr("fill", colors.kleenway.green)
      .style("opacity", 0.9)
      .on("mouseover", function (this: SVGRectElement, event, d) {
        d3.select(this).style("opacity", 1).style("cursor", "pointer");

        const tooltip = svg
          .append("g")
          .attr("class", "tooltip")
          .attr("transform", `translate(${x(d.month)! + x.bandwidth() / 2},${y(d.completedCount) - 8})`);

        tooltip
          .append("rect")
          .attr("x", -42)
          .attr("y", -24)
          .attr("width", 84)
          .attr("height", 20)
          .attr("fill", "rgba(0,0,0,0.8)")
          .attr("rx", 4);

        tooltip
          .append("text")
          .attr("text-anchor", "middle")
          .attr("y", -10)
          .attr("fill", "white")
          .attr("font-size", "12px")
          .text(`${d.completedCount}`);
      })
      .on("mouseout", function (this: SVGRectElement) {
        d3.select(this).style("opacity", 0.9);
        svg.selectAll(".tooltip").remove();
      })
      .transition()
      .duration(700)
      .delay((_, i) => i * 40)
      .ease(d3.easeQuadOut)
      .attr("y", (d) => y(d.completedCount))
      .attr("height", (d) => height - y(d.completedCount));

    svg
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", 0 - margin.left + 16)
      .attr("x", 0 - height / 2)
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .style("font-size", "12px")
      .style("fill", colors.grey.dark)
      .text("Completed Work Orders");
  }, [completedWorkData]);

  return (
    <WidgetContainer>
      <Stack gap={3}>
        <HeaderRow>
          <h1 style={{ fontWeight: 600, margin: 0 }}>{caption}</h1>
          <StaticDataBadge>Static Data</StaticDataBadge>
        </HeaderRow>
        <ChartContainer>
          <svg ref={svgRef}></svg>
        </ChartContainer>
      </Stack>
    </WidgetContainer>
  );
};

export default InternallyCompletedWorkChart;

const WidgetContainer = styled.div`
  display: flex;
  border: 1px solid ${colors.grey.light11};
  border-top: 4px solid ${colors.kleenway.green};
  padding: 16px;
  border-radius: 6px;
`;

const ChartContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;

const StaticDataBadge = styled.span`
  background-color: ${colors.grey.light11};
  color: ${colors.grey.dark};
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`;
